import { useState } from "react";
import { ArrowRight, Lock } from "lucide-react";
import { LOGO_URL } from "../../data/quizData";

export const LeadCaptureStep = ({ onSubmit, totalScore, maxScore }) => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    website: "",
    growthChallenge: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email) return;
    
    setIsSubmitting(true);
    await onSubmit(formData);
    setIsSubmitting(false);
  };

  const isValid = formData.name && formData.email && formData.growthChallenge;
  const scorePercentage = Math.round((totalScore / maxScore) * 100);

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12">
      <div className="max-w-xl w-full">
        {/* Logo */}
        <div className="flex justify-center mb-8 animate-fadeIn">
          <img src={LOGO_URL} alt="Zaharlab" className="h-14 w-auto" />
        </div>

        {/* Header */}
        <div className="text-center mb-8 animate-fadeIn delay-100">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-cyan-500/10 border border-cyan-500/20 text-cyan-400 text-sm font-medium mb-6">
            <Lock className="w-4 h-4" />
            Audit Complete
          </div>
          
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-3 tracking-tight">
            Your Assessment is Ready
          </h2>
          <p className="text-slate-400 text-lg">
            Enter your details to unlock your strategic assessment and personalized recommendations.
          </p>
        </div>

        {/* Score Preview */}
        <div className="glass rounded-xl p-6 mb-8 text-center animate-slideInUp delay-200">
          <p className="text-slate-400 text-sm uppercase tracking-wider mb-2">Preliminary Score</p>
          <div className="flex items-baseline justify-center gap-2">
            <span className="text-5xl font-bold gradient-text font-mono">{totalScore}</span>
            <span className="text-2xl text-slate-500">/ {maxScore}</span>
          </div>
          <div className="mt-4 score-bar-container">
            <div 
              className="score-bar-fill"
              style={{ width: `${scorePercentage}%` }}
            />
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="stage-card animate-slideInUp delay-300">
          <div className="space-y-5">
            {/* Name */}
            <div>
              <label htmlFor="name" className="block text-slate-300 font-medium mb-2">
                Full Name <span className="text-red-400">*</span>
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="John Smith"
                className="zaharlab-input"
                required
                data-testid="lead-name-input"
              />
            </div>

            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-slate-300 font-medium mb-2">
                Business Email <span className="text-red-400">*</span>
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="john@company.com"
                className="zaharlab-input"
                required
                data-testid="lead-email-input"
              />
            </div>

            {/* Website */}
            <div>
              <label htmlFor="website" className="block text-slate-300 font-medium mb-2">
                Website
              </label>
              <input
                type="url"
                id="website"
                name="website"
                value={formData.website}
                onChange={handleChange}
                placeholder="https://yourcompany.com"
                className="zaharlab-input"
                data-testid="lead-website-input"
              />
            </div>

            {/* Growth Challenge */}
            <div>
              <label htmlFor="growthChallenge" className="block text-slate-300 font-medium mb-2">
                Biggest Growth Challenge <span className="text-red-400">*</span>
              </label>
              <textarea
                id="growthChallenge"
                name="growthChallenge"
                value={formData.growthChallenge}
                onChange={handleChange}
                placeholder="Describe your primary obstacle to scaling..."
                className="zaharlab-input zaharlab-textarea"
                required
                data-testid="lead-challenge-input"
              />
            </div>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={!isValid || isSubmitting}
            className={`cta-button w-full mt-8 ${
              !isValid || isSubmitting ? "opacity-50 cursor-not-allowed" : ""
            }`}
            data-testid="lead-submit-btn"
          >
            {isSubmitting ? "Processing..." : "Unlock My Assessment"}
            <ArrowRight className="ml-2 w-5 h-5" />
          </button>
        </form>

        {/* Privacy note */}
        <p className="text-center text-slate-500 text-sm mt-6 animate-fadeIn delay-400">
          Your information is secure and will only be used to deliver your assessment.
        </p>
      </div>
    </div>
  );
};
